import { BaseService } from './BaseService.js'

/**
 * Folder Service
 * Handles folder operations for organizing test cases
 */
export class FolderService extends BaseService {
  constructor() {
    super('folders')
  }

  /**
   * Create a new folder
   */
  async createFolder(data) {
    if (!this.db) {
      throw new Error('Database service not initialized in FolderService')
    }

    const folderData = {
      name: data.name.trim(),
      description: data.description || '',
      parent_id: data.parentId || null,
      project_id: data.projectId,
      created_by: data.created_by,
      created_at: new Date(),
      updated_at: new Date(),
    }

    return await this.create(folderData)
  }

  /**
   * Get folders by project (only non-deleted)
   * Backend now defaults to excluding soft-deleted records, so no client-side
   * is_deleted filter is needed.
   */
  async getByProject(projectId) {
    if (!this.db) {
      throw new Error('Database service not initialized in FolderService')
    }

    const folders = await this.findBy('project_id', projectId)
    // Map database field names to UI field names
    return folders.map(folder => ({
      ...folder,
      parentId: folder.parent_id,
      projectId: folder.project_id,
    }))
  }

  /**
   * Get root folders (no parent)
   */
  async getRootFolders(projectId) {
    return await this.findBy('parent_id', null, {
      additionalFilters: [['project_id', '==', projectId]],
    })
  }

  /**
   * Get child folders
   */
  async getChildFolders(parentId) {
    return await this.findBy('parent_id', parentId)
  }

  /**
   * Build folder tree structure
   */
  async getFolderTree(projectId) {
    const allFolders = await this.getByProject(projectId)
    return this.buildTree(allFolders)
  }

  /**
   * Build hierarchical tree from flat array
   */
  buildTree(folders, parentId = null) {
    return folders
      .filter(folder => folder.parent_id === parentId)
      .map(folder => ({
        ...folder,
        children: this.buildTree(folders, folder.id),
      }))
  }

  /**
   * Move folder to new parent
   */
  async moveFolder(folderId, newParentId) {
    const updateData = {
      parent_id: newParentId,
      updated_at: new Date(),
    }

    return await this.update(folderId, updateData)
  }

  /**
   * Get folder path (breadcrumb)
   */
  async getFolderPath(folderId) {
    const path = []
    let currentId = folderId

    while (currentId) {
      const folder = await this.getById(currentId)
      if (!folder) break

      path.unshift(folder)
      currentId = folder.parent_id
    }

    return path
  }

  /**
   * Get all descendants of a folder
   */
  async getDescendants(folderId) {
    const descendants = []
    const children = await this.getChildFolders(folderId)

    for (const child of children) {
      descendants.push(child)
      const childDescendants = await this.getDescendants(child.id)
      descendants.push(...childDescendants)
    }

    return descendants
  }

  /**
   * Soft delete folder
   */
  async softDelete(folderId, userId) {
    try {
      // Use dedicated soft-delete endpoint (bypasses update() safeguard)
      const { http } = await import('@/utils/httpClient')
      const response = await http.post(`/folders/${folderId}/soft-delete`, {
        deletedBy: userId,
      })
      return response
    } catch (error) {
      console.error('Error in FolderService.softDelete:', error)
      console.error('Error stack:', error.stack)
      throw error
    }
  }

  /**
   * Soft delete folder and all descendants
   */
  async softDeleteWithDescendants(folderId, userId) {
    // Get all descendants first
    const descendants = await this.getDescendants(folderId)

    // Soft delete all descendants
    for (const descendant of descendants) {
      await this.softDelete(descendant.id, userId)
    }

    // Soft delete the folder itself
    return await this.softDelete(folderId, userId)
  }

  /**
   * Restore folder from soft delete
   */
  async restore(folderId) {
    try {
      // Check if folder exists
      const folder = await this.getById(folderId)
      if (!folder) {
        throw new Error(`Folder not found with ID: ${folderId}`)
      }

      // Merge with existing folder data to preserve required fields
      const updateData = {
        ...folder,
        is_deleted: false,
        deleted_at: null,
        deleted_by: null,
        updated_at: new Date(),
      }

      // Remove fields that shouldn't be updated
      delete updateData.id
      delete updateData.created_at
      delete updateData._id

      return await this.update(folderId, updateData)
    } catch (error) {
      console.error('Error in FolderService.restore:', error)
      throw error
    }
  }

  /**
   * Restore folder and all descendants
   */
  async restoreWithDescendants(folderId) {
    try {
      // Get all descendants first (including soft deleted ones)
      const folder = await this.getById(folderId)
      if (!folder) {
        throw new Error('Folder not found')
      }

      // Get all folders in the project (including deleted) for descendant resolution.
      // Uses include_deleted to bypass the backend default soft-delete filter.
      await this._ensureDatabaseService()
      const allFolders = await this.findBy('project_id', folder.project_id, {
        additionalFilters: [['include_deleted', '==', 'true']],
      })

      // Find all descendants recursively
      const findDescendants = (parentId, folders) => {
        const children = folders.filter(f => f.parent_id === parentId)
        let result = [...children]
        for (const child of children) {
          result = result.concat(findDescendants(child.id, folders))
        }
        return result
      }

      const descendants = findDescendants(folderId, allFolders)

      // Restore all descendants
      for (const descendant of descendants) {
        await this.restore(descendant.id)
      }

      // Restore the folder itself
      return await this.restore(folderId)
    } catch (error) {
      console.error('Error in restoreWithDescendants:', error)
      throw error
    }
  }

  /**
   * Get ALL folders by project (including deleted ones)
   * Used for duplicate checking during imports.
   * Passes include_deleted to bypass the backend default soft-delete filter.
   */
  async getAllByProject(projectId) {
    if (!this.db) {
      throw new Error('Database service not initialized in FolderService')
    }

    const folders = await this.findBy('project_id', projectId, {
      additionalFilters: [['include_deleted', '==', 'true']],
    })
    // Return ALL folders (including deleted) and map database field names to UI field names
    return folders.map(folder => ({
      ...folder,
      parentId: folder.parent_id,
      projectId: folder.project_id,
    }))
  }

  /**
   * Get deleted folders by project
   * Explicitly requests is_deleted=true from the backend so the query returns
   * only soft-deleted records (no client-side filtering needed).
   */
  async getDeletedByProject(projectId, options: Record<string, any> = {}) {
    try {
      const folders = await this.findBy('project_id', projectId, {
        ...options,
        additionalFilters: [...(options.additionalFilters || []), ['is_deleted', '==', 'true']],
      })

      // Map database field names to UI field names
      return folders.map(folder => ({
        ...folder,
        parentId: folder.parent_id || folder.parentId,
        projectId: folder.project_id,
      }))
    } catch (error) {
      console.error('Error getting deleted folders:', error)
      throw error
    }
  }

  /**
   * Delete folder and all descendants (hard delete)
   */
  async deleteFolder(folderId) {
    // Get all descendants first
    const descendants = await this.getDescendants(folderId)

    // Delete all descendants
    for (const descendant of descendants) {
      await this.delete(descendant.id)
    }

    // Delete the folder itself
    return await this.delete(folderId)
  }

  /**
   * Validate folder name uniqueness within parent
   */
  async validateFolderName(name, projectId, parentId = null, excludeId = null) {
    const existingFolders = await this.findBy('parent_id', parentId, {
      additionalFilters: [
        ['project_id', '==', projectId],
        ['name', '==', name.trim()],
      ],
    })

    if (excludeId) {
      return existingFolders.filter(f => f.id !== excludeId).length === 0
    }

    return existingFolders.length === 0
  }

  /**
   * Remove color fields from all existing folders
   * This migration ensures all folders use consistent default appearance
   */
  async removeColorFields() {
    if (!this.db) {
      throw new Error('Database service not initialized in FolderService')
    }

    // Get all folders
    const allFolders = await this.getAll()

    if (allFolders.length === 0) {
      return { updated: 0, skipped: 0, total: 0 }
    }

    let updatedCount = 0
    let skippedCount = 0

    for (const folder of allFolders) {
      try {
        // Check if folder has color field
        if (folder.color !== undefined) {
          // Remove color field and update timestamp
          const updateData = {
            updated_at: new Date(),
          }

          // Remove color field from the folder object
          delete folder.color

          // Update folder in database
          await this.update(folder.id, updateData)
          updatedCount++
        } else {
          skippedCount++
        }
      } catch (error) {
        // Skip folders that can't be updated
      }
    }

    // Return summary

    return {
      updated: updatedCount,
      skipped: skippedCount,
      total: allFolders.length,
    }
  }
}

export const folderService = new FolderService()
